import { noticeParts } from '../../lib/notice-link';
import { formatMoney } from '../../lib/money';
import { DocLogo } from './DocLogo';

export interface InvoicePreviewLine {
  qty: number;
  description: string;
  unitPence: number;
  amountPence: number;
  orderRef?: string;
}

export function orderRef(poNumber?: string, lineRef?: string): string {
  return [poNumber, lineRef].map((part) => (part || '').trim()).filter(Boolean).join(' / ');
}

function dottedDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return match ? `${match[3]}.${match[2]}.${match[1]}` : '';
}

function NoticeText({ value }: { value: string }) {
  return (
    <span className="whitespace-pre-line">
      {noticeParts(value).map((part, index) => part.href
        ? <a key={index} href={part.href} className="underline" style={{ color: '#1155cc' }} target="_blank" rel="noreferrer">{part.text}</a>
        : <span key={index}>{part.text}</span>)}
    </span>
  );
}

const BANK: [string, string][] = [
  ['Bank Name', 'Revolut Business'],
  ['Bank address', '7 Westferry Circus, Canary Wharf, London, E14 4HD'],
  ['Account Name', 'MaxIron Ltd'],
  ['Account No.', '40300404'],
  ['Sort Code', '040075'],
  ['IBAN', 'GB13REVO00996970545792'],
];

export const PO_BUYER = {
  name: 'Maxiron Ltd',
  address: '3rd Floor 86-90 Paul Street, London, EC2A 4NE',
  company: 'Company No. 14444817',
  vat: 'VAT No. 435737379',
  email: 'office@maxiron.com',
};

export function invoiceTerms(row: {
  validDays?: number;
  paymentTerms?: string;
  delivery?: string;
  purpose?: string;
  scope?: string;
  notice?: string;
  acceptance?: string;
}): [string, string][] {
  return ([
    ['Validity', row.validDays && row.validDays > 0 ? `${row.validDays} days` : ''],
    ['Payment terms', row.paymentTerms || ''],
    ['Delivery', row.delivery || ''],
    ['Purpose', row.purpose || ''],
    ['Scope & exclusions', row.scope || ''],
    ['Terms & conditions', row.notice || ''],
    ['To proceed', row.acceptance || ''],
  ] as [string, string][]).filter((item) => item[1].trim());
}

export function InvoicePreview({
  documentTitle = 'INVOICE',
  numberLabel = 'INVOICE NO.',
  number,
  customerName,
  billingEmails,
  billToName = '',
  billToAddress = '',
  billToEmail = '',
  billToDetails = '',
  bankLines,
  title = '',
  issuedOn,
  dueOn,
  currency,
  poNumber,
  contactName,
  contactRole = '',
  contactEmail = '',
  contactPhone = '',
  validDays = 0,
  paymentTerms = '',
  delivery = '',
  acceptance = '',
  purpose = '',
  scope = '',
  notice,
  lines,
  vatPence,
}: {
  documentTitle?: string;
  numberLabel?: string;
  number?: string;
  customerName: string;
  billingEmails: string[];
  billToName?: string;
  billToAddress?: string;
  billToEmail?: string;
  billToDetails?: string;
  bankLines?: [string, string][];
  title?: string;
  issuedOn: string;
  dueOn: string;
  currency: string;
  poNumber: string;
  contactName: string;
  contactRole?: string;
  contactEmail?: string;
  contactPhone?: string;
  validDays?: number;
  paymentTerms?: string;
  delivery?: string;
  acceptance?: string;
  purpose?: string;
  scope?: string;
  notice: string;
  lines: InvoicePreviewLine[];
  vatPence?: number | null;
}) {
  const money = currency || 'GBP';
  const shown = lines.filter((line) => line.description || line.amountPence);
  const net = shown.reduce((sum, line) => sum + (line.amountPence || 0), 0);
  const vat = vatPence ?? 0;
  const vatLabel = vat !== 0 && net > 0 && Math.abs(vat - Math.round(net * 0.2)) <= 2 ? 'VAT @20%' : 'VAT';
  const vatText = vat === 0 ? 'NONE' : formatMoney(vat, money);
  const gross = net + (vat === 0 ? 0 : vat);
  const invoiceDocument = documentTitle === 'INVOICE' || documentTitle === 'CREDIT NOTE';
  const purchaseOrder = documentTitle === 'PURCHASE ORDER';
  const showParty = invoiceDocument || purchaseOrder;
  const address = billToAddress.split(/\n|,/).map((part) => part.trim()).filter(Boolean);
  const emails = showParty && billToEmail ? [billToEmail] : billingEmails;
  const to = showParty
    ? [billToName || 'Accounts Payable', customerName, ...address, ...emails.map((email) => `e: ${email}`)].filter(Boolean)
    : ['Accounts Payable', customerName, ...billingEmails.map((email) => `e: ${email}`)].filter(Boolean);
  const terms = showParty ? invoiceTerms({ validDays, paymentTerms, delivery, purpose, scope, notice, acceptance }) : [];
  const bank = bankLines?.length ? bankLines : BANK;
  const orderDocument = documentTitle !== 'INVOICE';
  const showLineOrder = !orderDocument || shown.some((line) => line.orderRef);
  const leadSpan = showLineOrder ? 3 : 2;
  const blankLine = { qty: 0, description: '', unitPence: 0, amountPence: 0, orderRef: '' };

  return (
    <article className="doc-sheet doc-quotation">
      <div className="flex items-start justify-between gap-4">
        <DocLogo />
        <h1 className="text-[22pt] font-bold leading-none">{documentTitle}</h1>
      </div>
      <div className="mt-2 flex items-start justify-between gap-4">
        <div>
          <p className="text-[11pt] font-bold">MaxIron Ltd</p>
          <p>Company No.: 14444817 &nbsp; VAT No.: 435737379</p>
          <p>Address: 3rd Floor 86-90 Paul Street, London, EC2A 4NE</p>
          <p>Contact: office@maxiron.com</p>
        </div>
        <div className="text-right">
          <p>{numberLabel} {number || 'Draft'}</p>
          <p>DATE: {dottedDate(issuedOn)}</p>
          {invoiceDocument && dueOn ? <p>DUE: {dottedDate(dueOn)}</p> : null}
        </div>
      </div>
      {purchaseOrder ? (
        <div className="mt-4 flex items-start gap-6">
          <div className="w-1/2">
            <p className="font-bold">SUPPLIER</p>
            <p>{billToName || customerName || 'Supplier'}</p>
            {address.map((line) => <p key={line}>{line}</p>)}
            {emails.map((email) => <p key={email}>e: <a className="underline" href={`mailto:${email}`}>{email}</a></p>)}
          </div>
          <div className="w-1/2">
            <p className="font-bold">BILL TO</p>
            <p>{PO_BUYER.name}</p>
            <p>{PO_BUYER.address}</p>
            <p>{PO_BUYER.company} · {PO_BUYER.vat}</p>
            <p>e: <a className="underline" href={`mailto:${PO_BUYER.email}`}>{PO_BUYER.email}</a></p>
          </div>
        </div>
      ) : showParty ? (
        <div className="mt-4 flex items-start gap-6">
          <div className="w-1/2">
            <p><span className="font-bold">TO</span> {billToName || 'Accounts Payable'}</p>
            {!address.length && customerName ? <p>{customerName}</p> : null}
            {address.map((line) => <p key={line}>{line}</p>)}
            {emails.map((email) => <p key={email}>e: <a className="underline" href={`mailto:${email}`}>{email}</a></p>)}
          </div>
          <div className="w-1/2 text-right">
            {title ? <h2 className="text-[11pt] font-bold">{title}</h2> : null}
            {billToDetails ? <p className="mt-2 whitespace-pre-line text-[10pt]">{billToDetails}</p> : null}
          </div>
        </div>
      ) : <p className="mt-4"><span className="font-bold">TO</span> {to.join(', ')}</p>}
      <table className="doc-grid mt-3">
        <thead>
          <tr>{(invoiceDocument || purchaseOrder ? ['CONTACT', 'ROLE', 'EMAIL', 'TELEPHONE'] : orderDocument ? ['CONTACT', 'PURCHASE ORDER', 'PAYMENT TERMS', 'DUE DATE'] : ['CONTACT', 'PAYMENT TERMS', 'DUE DATE']).map((label) => <th key={label}>{label}</th>)}</tr>
        </thead>
        <tbody>
          <tr>
            {invoiceDocument || purchaseOrder ? (
              <>
                <td>{contactName}</td>
                <td>{contactRole}</td>
                <td>{contactEmail}</td>
                <td>{contactPhone}</td>
              </>
            ) : (
              <>
                <td>{contactName}</td>
                {orderDocument ? <td>{poNumber}</td> : null}
                <td />
                <td>{dottedDate(dueOn)}</td>
              </>
            )}
          </tr>
        </tbody>
      </table>
      <table className="doc-grid mt-3">
        <thead>
          <tr>
            <th className="w-12">QTY</th>
            {showLineOrder ? <th className="w-28">PO / LINE</th> : null}
            <th>DESCRIPTION</th>
            <th className="right w-28">UNIT PRICE</th>
            <th className="right w-28">LINE TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {(shown.length ? shown : [blankLine]).map((line, index) => (
            <tr key={index}>
              <td>{line.qty || ''}</td>
              {showLineOrder ? <td>{line.orderRef || ''}</td> : null}
              <td className="whitespace-pre-line">{line.description}</td>
              <td className="right">{line.unitPence > 0 ? formatMoney(line.unitPence, money) : line.description ? '-' : ''}</td>
              <td className="right">{line.amountPence ? formatMoney(line.amountPence, money) : ''}</td>
            </tr>
          ))}
          <tr><td>&nbsp;</td>{showLineOrder ? <td /> : null}<td /><td /><td /></tr>
          <tr><td>&nbsp;</td>{showLineOrder ? <td /> : null}<td>{invoiceDocument ? '' : <NoticeText value={notice} />}</td><td /><td /></tr>
          <tr>
            <td colSpan={leadSpan} className="text-center">{purchaseOrder ? 'Quote this purchase order on your invoice.' : 'Please remit using the bank details below.'}</td>
            <td className="right font-bold">SUBTOTAL</td>
            <td className="right font-bold">{formatMoney(net, money)}</td>
          </tr>
          <tr>
            <td colSpan={leadSpan} />
            <td className={`right${vat === 0 ? ' font-bold' : ''}`}>{vatLabel}</td>
            <td className={`right${vat === 0 ? ' font-bold' : ''}`}>{vatText}</td>
          </tr>
          <tr>
            <td colSpan={leadSpan} />
            <td className="right font-bold">TOTAL</td>
            <td className="right font-bold">{formatMoney(gross, money)}</td>
          </tr>
        </tbody>
      </table>
      {terms.length ? (
        <section className="mt-4 text-[9pt] leading-relaxed">
          <h2 className="mb-2 text-[11pt] font-bold">Commercial terms</h2>
          <dl className="grid grid-cols-[7.2rem_1fr] gap-x-3 gap-y-1">
            {terms.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="font-bold">{label}</dt>
                <dd><NoticeText value={value} /></dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      {purchaseOrder ? null : (
      <table className="doc-grid mt-4">
        <tbody>
          {bank.map(([label, value]) => (
            <tr key={label}><td className="left w-28 font-bold">{label}</td><td className="left">{value}</td></tr>
          ))}
        </tbody>
      </table>
      )}
      <div className="mt-auto px-3 py-4 text-center text-[14pt]" style={{ background: '#E7D8F5' }}>
        <p>MaxIron values your business and the relationship between our organisations.</p>
        <p className="mt-1">Your Success Drives our Business.</p>
      </div>
    </article>
  );
}
